/* The Organic Butchery — theme.js
   Islands only: cart drawer, mobile menu + Cart API. No frameworks.
   Loaded as a deferred module; adds no work until first interaction. */

/* ---------- slide-out panel controller (cart drawer + mobile menu) ---------- */

function createPanel(panel, scrim, trigger) {
  if (!panel || !scrim) return null;
  let lastFocus = null;

  function onKeydown(e) {
    if (e.key === 'Escape') close();
    if (e.key === 'Tab') trap(e);
  }

  function trap(e) {
    const focusables = panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  function open() {
    lastFocus = document.activeElement;
    scrim.hidden = false;
    requestAnimationFrame(() => {
      scrim.classList.add('is-open');
      panel.classList.add('is-open');
    });
    document.body.classList.add('drawer-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    panel.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    /* commit any pending cart quantity change before the drawer goes away
       (no-op for the menu/filter panels, which have no line timers) */
    flushLineChanges();
    scrim.classList.remove('is-open');
    panel.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown);
    scrim.addEventListener('transitionend', () => { scrim.hidden = true; }, { once: true });
    if (lastFocus) lastFocus.focus();
  }

  return { open, close, panel, scrim };
}

const cartPanel = createPanel(
  document.querySelector('[data-cart-drawer]'),
  document.querySelector('[data-drawer-scrim]')
);
const menuPanel = createPanel(
  document.querySelector('[data-menu-drawer]'),
  document.querySelector('[data-menu-scrim]'),
  document.querySelector('[data-menu-open]')
);
const filterPanel = createPanel(
  document.querySelector('[data-filter-drawer]'),
  document.querySelector('[data-filter-scrim]'),
  document.querySelector('[data-filter-open]')
);

/* ---------- cart state ---------- */

async function refreshDrawer() {
  if (!cartPanel) return;
  const res = await fetch(`${window.routes.cart_url}?section_id=cart-drawer-content`);
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('.shopify-section') || doc.body;
  const target = cartPanel.panel.querySelector('[data-cart-drawer-content]');
  if (!target) return;
  target.innerHTML = fresh.innerHTML;
  syncCountFromDrawer(target);
  reinitPaymentButtons();
  document.dispatchEvent(new CustomEvent('cart:refreshed'));
}

/* read the header count straight from the drawer markup we just rendered,
   so a single add/change request keeps the badge in sync */
function syncCountFromDrawer(target) {
  const marker = target && target.querySelector('[data-cart-total]');
  if (marker) updateCount(parseInt(marker.dataset.cartTotal, 10) || 0);
}

// After an AJAX cart re-render, re-initialise the accelerated checkout buttons
// (Shop Pay / Apple Pay / Google Pay) or they render dead. No-op if unavailable.
function reinitPaymentButtons() {
  try {
    if (window.Shopify && window.Shopify.PaymentButton && Shopify.PaymentButton.init) {
      Shopify.PaymentButton.init();
    }
  } catch (e) {}
}

function updateCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; });
  document.querySelectorAll('[data-cart-count-wrap]').forEach((el) => {
    el.classList.toggle('is-active', count > 0);
  });
}

async function refreshCartPage() {
  const page = document.querySelector('[data-cart-page]');
  if (!page) return;
  const res = await fetch(`${window.routes.cart_url}?section_id=${page.dataset.sectionId}`);
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-page]');
  if (fresh) {
    page.innerHTML = fresh.innerHTML;
    reinitPaymentButtons();
    document.dispatchEvent(new CustomEvent('cart:refreshed'));
  }
}

async function changeLine(line, quantity) {
  const res = await fetch(`${window.routes.cart_change_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, quantity }),
  });
  if (!res.ok) return;
  const cart = await res.json();
  updateCount(cart.item_count);
  await Promise.all([refreshDrawer(), refreshCartPage()]);
}

/* Debounced quantity change: the +/- buttons update the number instantly, but
   the cart request only fires ~500ms after the last tap — so rapid multi-clicks
   batch into a single update instead of one round-trip (and re-render) each.
   flushLineChanges() fires any pending change immediately (e.g. on drawer close)
   so nothing runs against a closed drawer. */
const lineTimers = {};
function queueLineChange(line, quantity, lineEl) {
  if (lineTimers[line]) clearTimeout(lineTimers[line].id);
  const run = () => {
    delete lineTimers[line];
    if (lineEl && lineEl.isConnected) lineEl.classList.add('is-updating');
    changeLine(line, quantity);
  };
  lineTimers[line] = { id: setTimeout(run, 500), run };
}
function flushLineChanges() {
  Object.values(lineTimers).forEach(({ id, run }) => { clearTimeout(id); run(); });
}

/* Apply one selling plan (or clear it) across every eligible cart line.
   Skips lines already on the target plan; uses the last change.js payload
   for the count so we avoid an extra /cart.js round-trip. */
let cartPlanBusy = false;
async function changeCartPlan(sellingPlan, wrap) {
  if (cartPlanBusy) return;
  const node = (wrap && wrap.querySelector('[data-cart-sub-lines]'))
    || document.querySelector('[data-cart-sub-lines]');
  if (!node) return;
  let lines = [];
  try { lines = JSON.parse(node.textContent); } catch { return; }
  if (!lines.length) return;

  const plan = sellingPlan ? String(sellingPlan) : null;
  /* the drawer's "update your subscription" box only changes lines that are
     already subscriptions; one-time items stay one-time */
  const scope = (wrap && wrap.dataset.cartPlanScope)
    || node.closest('[data-cart-sub]')?.dataset.cartPlanScope;
  if (scope === 'subscribed') lines = lines.filter((line) => line.plan);
  const pending = lines.filter((line) => String(line.plan || '') !== String(plan || ''));

  cartPlanBusy = true;
  document.querySelectorAll('[data-cart-sub]').forEach((el) => el.classList.add('is-updating'));

  try {
    if (!pending.length) {
      document.querySelectorAll('[data-cart-sub]').forEach((el) => el.classList.remove('is-updating'));
      return;
    }

    let cart = null;
    for (const line of pending) {
      const res = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.qty, selling_plan: plan }),
      });
      if (!res.ok) break;
      cart = await res.json();
    }

    if (cart) {
      updateCount(cart.item_count);
      await Promise.all([refreshDrawer(), refreshCartPage()]);
    } else {
      /* rejected: re-render so the select shows the basket's real frequency again */
      document.querySelectorAll('[data-cart-sub]').forEach((el) => el.classList.remove('is-updating'));
      await Promise.all([refreshDrawer(), refreshCartPage()]);
    }
  } finally {
    cartPlanBusy = false;
  }
}

/* ---------- one delivery frequency per basket ----------
   Subscription lines on different frequencies make checkout fail ("Your order
   cannot be shipped to the selected address" — see snippets/cart-plan-check.liquid),
   so a basket keeps one plan. The product page pre-selects the basket's plan and,
   when the customer picks another, tells them the basket will follow (forcedPlan).
   For baskets that end up mixed any other way, the basket's own plan wins (the one
   most lines are on). Runs whenever the drawer or cart page carries
   the mixed-plan marker, and once more before checkout. */
let planAlign = null;        // the alignment in flight, so callers share it
let planAlignTried = '';     // basket state already attempted (stops retry loops)
let forcedPlan = null;       // new frequency the basket follows, while that add is in flight

function alignCartPlans(preferredPlan) {
  if (!planAlign) {
    planAlign = runAlignCartPlans(preferredPlan || forcedPlan).finally(() => { planAlign = null; });
  }
  return planAlign;
}

async function runAlignCartPlans(preferredPlan) {
  let cart;
  try {
    const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return false;
    cart = await res.json();
  } catch { return false; }

  const planOf = (item) => String(item.selling_plan_allocation.selling_plan.id);
  const subs = cart.items.filter((item) => item.selling_plan_allocation);
  if (new Set(subs.map(planOf)).size < 2) return false;

  const state = subs.map((item) => `${item.key}:${planOf(item)}`).join('|');
  if (state === planAlignTried) return false;
  planAlignTried = state;

  /* the plan the customer asked for; otherwise the one most lines are on, and on a
     tie the oldest line's (cart.js lists the newest first) — curated lines stay put */
  const counts = {};
  subs.forEach((item) => { counts[planOf(item)] = (counts[planOf(item)] || 0) + 1; });
  const top = Math.max(...Object.values(counts));
  const lead = subs.find((item) => planOf(item) === String(preferredPlan))
    || [...subs].reverse().find((item) => counts[planOf(item)] === top);
  const target = planOf(lead);
  const targetName = lead.selling_plan_allocation.selling_plan.name;

  let changed = false;
  for (const item of subs) {
    if (planOf(item) === target) continue;
    let ok = await setLinePlan(item, target);
    if (!ok) {
      /* a product outside the shared plan group: use its own plan of the same name */
      const own = await planIdByName(item, targetName);
      if (own) ok = await setLinePlan(item, own);
    }
    changed = changed || ok;
  }

  if (changed) {
    await Promise.all([refreshDrawer(), refreshCartPage()]);
    showPlanNote(targetName);
  }
  return changed;
}

async function setLinePlan(item, planId) {
  try {
    const res = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, selling_plan: planId }),
    });
    return res.ok;
  } catch { return false; }
}

async function planIdByName(item, name) {
  try {
    const res = await fetch(`${item.url.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const product = await res.json();
    const want = name.trim().toLowerCase();
    for (const group of product.selling_plan_groups || []) {
      const plan = (group.selling_plans || []).find((p) => p.name.trim().toLowerCase() === want);
      if (plan) return String(plan.id);
    }
  } catch {}
  return null;
}

/* tell the customer why a line's frequency changed (drawer and cart page);
   a small dismissible banner under the basket heading */
function showPlanNote(planName) {
  document.querySelectorAll('.cart-drawer__head, .cart-page__head').forEach((head) => {
    head.parentElement.querySelectorAll('[data-plan-note]').forEach((el) => el.remove());
    const note = document.createElement('div');
    note.className = 'cart-plan-note';
    note.setAttribute('role', 'status');
    note.dataset.planNote = '';
    note.innerHTML = `
      <svg class="cart-plan-note__icon" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
        <path d="M15.5 7.5A6 6 0 0 0 4.3 6M4.5 12.5A6 6 0 0 0 15.7 14M4 3v3.2h3.2M16 17v-3.2h-3.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <p class="cart-plan-note__text">So everything arrives together, your subscription items are now all delivered <b></b>.</p>
      <button class="cart-plan-note__close" type="button" data-plan-note-close aria-label="Dismiss">
        <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>`;
    note.querySelector('b').textContent = planName.toLowerCase();
    head.after(note);
  });
}
document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-plan-note-close]')) return;
  document.querySelectorAll('[data-plan-note]').forEach((el) => el.remove());
});

const cartHasMixedPlans = () => Boolean(document.querySelector('[data-cart-plans-mixed]'));

/* a basket that is already mixed (e.g. built before this fix) is put right on load */
if (cartHasMixedPlans()) alignCartPlans();
document.addEventListener('cart:refreshed', () => { if (cartHasMixedPlans()) alignCartPlans(); });

/* last check before checkout: wait for any alignment, then continue */
document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.closest('[data-cart-drawer-content], [data-cart-page]')) return;
  const submitter = e.submitter;
  if (!submitter || submitter.name !== 'checkout') return;
  if (!planAlign && !cartHasMixedPlans()) return;
  e.preventDefault();
  submitter.setAttribute('aria-busy', 'true');
  submitter.classList.add('is-loading');
  planAlignTried = '';   // allow one more attempt for this basket
  await (planAlign || alignCartPlans());
  window.location.assign(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}checkout`);
});

/* ---------- basket frequency on Add to basket ----------
   The basket's plan is published by snippets/cart-plan-check.liquid inside the
   drawer (refreshed after every change). The product page pre-selects it; when the
   customer picks another frequency there, a hint says the rest of the basket will
   follow, so the add moves every subscription line to the new one. */
const basketPlan = () => {
  const el = document.querySelector('[data-cart-basket-plan]');
  return el ? { id: el.dataset.cartBasketPlan, name: el.dataset.cartBasketPlanName || '' } : null;
};

/* Appstle's storefront script wraps window.fetch and, on every /cart/add, overwrites
   selling_plan with whatever its own (hidden) widget has checked — nothing, as the
   theme replaces that widget — so subscriptions silently became one-time lines.
   Appstle skips any URL containing "?appstle=true" (its own opt-out), and Shopify
   ignores the extra parameter, so every theme add goes through this URL. */
const cartAddUrl = () => `${window.routes.cart_add_url}.js?appstle=true`;

async function addToCart(form) {
  const button = form.querySelector('[type="submit"]');
  if (button) {
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
  }

  try {
    /* One round-trip: add the line AND get the re-rendered drawer back via the
       Cart Sections API (was: add.js → cart.js → section fetch = three), so the
       drawer opens the moment the product is actually in the cart. */
    const body = new FormData(form);
    /* The subscription widget renders OUTSIDE the <form>, so its selected plan
       never lands in FormData. Read the chosen plan from the buy column and
       attach it, or "Subscribe & Save" silently adds a one-time line. */
    /* Product page v2 has its own Subscribe / One-time selector: its hidden
       input is the only source of truth. Drop anything else named
       selling_plan (Appstle injects a pre-checked one-time radio into the
       form), then add ours only while Subscription is chosen. */
    const ownPlan = form.querySelector('[data-pdp2-selling-plan]');
    if (ownPlan) {
      body.delete('selling_plan');
      if (!ownPlan.disabled && ownPlan.value) body.set('selling_plan', ownPlan.value);
    } else {
      const planScope = form.closest('.pdp__buy') || document;
      const planEl = planScope.querySelector('input[name="selling_plan"]:checked')
        || document.querySelector('input[name="selling_plan"]:checked')
        || planScope.querySelector('[name="selling_plan"]');
      const sellingPlan = planEl && !planEl.disabled ? (planEl.value || '') : '';
      if (sellingPlan) body.set('selling_plan', sellingPlan);
    }
    /* a frequency other than the basket's, chosen on the product page (where the
       hint said the basket follows): move the basket to it once added */
    const chosenPlan = body.get('selling_plan') || '';
    const basket = basketPlan();
    forcedPlan = chosenPlan && basket && basket.id !== chosenPlan && form.querySelector('[data-pdp2-selling-plan]')
      ? chosenPlan : null;

    body.append('sections', 'cart-drawer-content');
    body.append('sections_url', window.location.pathname);
    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      reportFormError(form, (data && data.description) || 'Something went wrong — please try again.');
      return;
    }
    const html = data && data.sections && data.sections['cart-drawer-content'];
    const target = cartPanel && cartPanel.panel.querySelector('[data-cart-drawer-content]');
    if (html && target) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('.shopify-section') || doc.body;
      target.innerHTML = fresh.innerHTML;
      syncCountFromDrawer(target);
      reinitPaymentButtons();
      document.dispatchEvent(new CustomEvent('cart:refreshed'));
    } else {
      await refreshDrawer();  /* fallback if sections came back empty */
    }
    if (cartPanel) cartPanel.open();   /* open once the product is in the cart */
    refreshCartPage();
    /* the customer picked a new frequency: move the rest of the basket onto it */
    if (forcedPlan) await alignCartPlans(forcedPlan);
  } finally {
    forcedPlan = null;
    if (button) {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }
}

function reportFormError(form, message) {
  let note = form.querySelector('[data-form-error]');
  if (!note) {
    note = document.createElement('p');
    note.setAttribute('data-form-error', '');
    note.setAttribute('role', 'alert');
    note.style.cssText = 'color:var(--red-deep);font-size:.85rem;margin-top:8px;';
    form.append(note);
  }
  note.textContent = message;
}

/* ---------- event delegation ---------- */

document.addEventListener('click', (e) => {
  if (cartPanel) {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); cartPanel.open(); return; }
    if (e.target.closest('[data-cart-close]') || e.target === cartPanel.scrim) { cartPanel.close(); return; }
  }

  /* cart line controls — drawer and cart page both mark buttons with data-line */
  const qtyBtn = e.target.closest('[data-qty-change][data-line]');
  if (qtyBtn) {
    const lineEl = qtyBtn.closest('.cart-line');
    const valueEl = lineEl.querySelector('[data-qty-value]');
    const current = parseInt(valueEl.value !== undefined ? valueEl.value : valueEl.textContent, 10) || 1;
    /* floor of 1 — emptying a line is the remove button's job, so a mis-tap on
       minus can never delete something the customer meant to keep */
    const next = Math.max(1, current + parseInt(qtyBtn.dataset.qtyChange, 10));
    if (next !== current) {
      if (valueEl.value !== undefined) valueEl.value = next; else valueEl.textContent = next; /* optimistic */
      queueLineChange(parseInt(qtyBtn.dataset.line, 10), next, lineEl); /* debounced sync */
    }
    return;
  }

  const removeBtn = e.target.closest('[data-line-remove][data-line]');
  if (removeBtn) {
    removeBtn.closest('.cart-line').classList.add('is-updating');
    changeLine(parseInt(removeBtn.dataset.line, 10), 0);
    return;
  }

  if (menuPanel) {
    if (e.target.closest('[data-menu-open]')) { e.preventDefault(); menuPanel.open(); return; }
    if (e.target.closest('[data-menu-close]') || e.target === menuPanel.scrim) { menuPanel.close(); return; }
  }

  if (filterPanel) {
    if (e.target.closest('[data-filter-open]')) { e.preventDefault(); filterPanel.open(); return; }
    if (e.target.closest('[data-filter-close]') || e.target === filterPanel.scrim) { filterPanel.close(); }
  }
});

/* Cart page: Subscribe & Save per item. Ticking puts that item on the basket's
   frequency (the summary box's select, so the basket keeps one plan); unticking
   makes it one-off. */
async function changeLinePlan(toggle) {
  if (cartPlanBusy) { toggle.checked = !toggle.checked; return; }
  const select = document.querySelector('[data-cart-page] [data-cart-plan]');
  const plan = toggle.checked ? ((select && select.value) || toggle.dataset.plan) : null;
  const line = toggle.closest('.cart-line');
  cartPlanBusy = true;
  line?.classList.add('is-updating');
  try {
    const res = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: toggle.dataset.key, quantity: Number(toggle.dataset.qty), selling_plan: plan }),
    });
    if (!res.ok) throw new Error(res.status);
    const cart = await res.json();
    updateCount(cart.item_count);
    await Promise.all([refreshDrawer(), refreshCartPage()]);
  } catch {
    toggle.checked = !toggle.checked;
  } finally {
    cartPlanBusy = false;
    line?.classList.remove('is-updating');
  }
}

/* Subscribe & Save boxes (drawer and cart page): the frequency select changes
   the subscription lines only (data-cart-plan-scope="subscribed") */
document.addEventListener('change', (e) => {
  const lineToggle = e.target.closest('[data-line-plan-toggle]');
  if (lineToggle) { changeLinePlan(lineToggle); return; }

  const select = e.target.closest('[data-cart-plan]');
  if (select && select.value) changeCartPlan(select.value, select.closest('[data-cart-sub]'));
});

/* ---------- predictive search ----------
   Debounced fetch against the Predictive Search API via section rendering;
   results render server-side by sections/predictive-search.liquid. */

const predInput = document.querySelector('[data-predictive-input]');
const predBox = document.querySelector('[data-predictive]');
if (predInput && predBox) {
  let timer = null;
  let controller = null;

  const closePred = () => {
    predBox.hidden = true;
    predInput.setAttribute('aria-expanded', 'false');
  };

  const fetchPred = async (q) => {
    if (controller) controller.abort();
    controller = new AbortController();
    try {
      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}` +
        '&resources[type]=product,article,page&resources[limit]=6' +
        '&resources[options][unavailable_products]=last&section_id=predictive-search';
      const res = await fetch(url, { signal: controller.signal });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-predictive-results]');
      if (!fresh) return;
      predBox.innerHTML = fresh.innerHTML;
      predBox.hidden = false;
      predInput.setAttribute('aria-expanded', 'true');
    } catch (err) {
      if (err.name !== 'AbortError') closePred();
    }
  };

  predInput.addEventListener('input', () => {
    clearTimeout(timer);
    const q = predInput.value.trim();
    if (q.length < 2) { closePred(); return; }
    timer = setTimeout(() => fetchPred(q), 250);
  });

  predInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePred();
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.site-search')) closePred();
  });
}

/* announcement bar rotation is CSS-only — see sections/announcement-bar.liquid */

/* ---------- Trustpilot lazy facade ----------
   The widget script loads once, only when a facade scrolls near view. */

const tpFacades = document.querySelectorAll('[data-trustpilot-facade]');
if (tpFacades.length) {
  let tpLoaded = false;
  const loadTrustpilot = () => {
    if (tpLoaded) return;
    tpLoaded = true;
    const s = document.createElement('script');
    s.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    s.async = true;
    document.head.append(s);
  };
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); loadTrustpilot(); }
    }, { rootMargin: '600px' });
    tpFacades.forEach((el) => io.observe(el));
  } else {
    loadTrustpilot();
  }
}

/* ---------- image hotspots (packaging panel) ---------- */

document.addEventListener('click', (e) => {
  const dot = e.target.closest('[data-hotspot]');
  const openSpots = document.querySelectorAll('.hotspot.is-open');

  if (dot) {
    const spot = dot.closest('.hotspot');
    const wasOpen = spot.classList.contains('is-open');
    openSpots.forEach((s) => {
      s.classList.remove('is-open');
      s.querySelector('[data-hotspot]').setAttribute('aria-expanded', 'false');
    });
    if (!wasOpen) {
      spot.classList.add('is-open');
      dot.setAttribute('aria-expanded', 'true');
    }
    return;
  }

  if (openSpots.length && !e.target.closest('.hotspot__card')) {
    openSpots.forEach((s) => {
      s.classList.remove('is-open');
      s.querySelector('[data-hotspot]').setAttribute('aria-expanded', 'false');
    });
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('.hotspot.is-open').forEach((s) => {
    s.classList.remove('is-open');
    s.querySelector('[data-hotspot]').setAttribute('aria-expanded', 'false');
  });
  // close any open header dropdown/mega and return focus to its trigger
  const navItem = document.activeElement?.closest?.('.site-nav__item');
  if (navItem) {
    navItem.classList.add('is-closed');
    navItem.querySelector('.site-nav__link')?.focus();
  }
});

/* the .is-closed latch (set on Esc) releases as soon as the pointer leaves
   or focus moves on, so the menu reopens normally next time */
document.querySelectorAll('.site-nav__item').forEach((item) => {
  item.addEventListener('mouseleave', () => item.classList.remove('is-closed'));
  item.addEventListener('focusout', (e) => {
    if (!item.contains(e.relatedTarget)) item.classList.remove('is-closed');
  });
});

/* social feed videos: Reel-style — play muted while on screen, pause off-screen */
const socialVideos = document.querySelectorAll('video.social__video');
if (socialVideos.length) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          target.muted = true;
          target.playsInline = true;
          target.play().catch(() => {});
        } else {
          target.pause();
        }
      });
    }, { threshold: 0.4 });
    socialVideos.forEach((v) => io.observe(v));
  } else {
    socialVideos.forEach((v) => { v.muted = true; v.play().catch(() => {}); });
  }
}

/* country selects: preselect the saved country (all_country_option_tags doesn't) */
document.querySelectorAll('select[data-default]').forEach((select) => {
  const value = select.dataset.default;
  if (!value) return;
  const option = Array.from(select.options).find((o) => o.value === value || o.textContent === value);
  if (option) option.selected = true;
});

/* nav-select: a dropdown whose option values are URLs (mobile tag filter) */
document.addEventListener('change', (e) => {
  if (!e.target.matches('[data-nav-select]')) return;
  if (e.target.value) window.location.assign(e.target.value);
});

/* collection sort: navigate with the chosen sort, preserving active filters */
document.addEventListener('change', (e) => {
  if (!e.target.matches('[data-sort-select]')) return;
  const url = new URL(window.location);
  url.searchParams.set('sort_by', e.target.value);
  url.searchParams.delete('page');
  window.location.assign(url);
});

/* discount code: Shopify applies codes via the /discount/CODE route */
document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-discount-form]');
  if (!form) return;
  e.preventDefault();
  const code = form.querySelector('input[name="discount"]').value.trim();
  if (code) window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.routes.cart_url)}`);
});

/* lazy product recommendations — PDP "Goes well with" + cart cross-sell */
document.querySelectorAll('[data-related][data-url]').forEach((related) => {
  const load = async () => {
    try {
      const res = await fetch(related.dataset.url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-related]');
      if (fresh && fresh.querySelector('.pcard')) related.replaceWith(fresh);
      else related.remove();
    } catch { related.remove(); }
  };
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { io.disconnect(); load(); }
    }, { rootMargin: '400px' });
    io.observe(related);
  } else {
    load();
  }
});

/* ---------- recently viewed ----------
   History is per-visitor so it cannot come from Liquid. Handles live in
   localStorage; the cards are fetched back through the Search
   section-rendering API (verified: `q=id:1 OR id:2` is supported) so they use
   the same product-card markup as every other grid rather than a second
   implementation in JS. */
(() => {
  const host = document.querySelector('[data-recently-viewed][data-section-id]');
  if (!host) return;

  const KEY = 'ob:recently-viewed';
  const KEEP = 12;        /* how many to remember */
  const MIN = 2;          /* a single card in a four-up row reads as a bug */
  const TIMEOUT = 8000;
  const editing = !!(window.Shopify && window.Shopify.designMode);

  /* Leave the node in place in the theme editor: removing it would drop the
     section out of the sidebar and the merchant could not select it. */
  const drop = () => { if (!editing) host.remove(); };

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY));
      /* tolerate anything stale or hand-edited in storage */
      return Array.isArray(raw)
        ? raw.filter((p) => p && typeof p.id === 'string' && typeof p.handle === 'string')
        : [];
    } catch { return []; }
  };

  /* Record this product FIRST, so a visitor who lands once and leaves still
     builds history for next time. Most-recent-first, de-duplicated, capped. */
  const id = host.dataset.currentId;
  const handle = host.dataset.currentHandle;
  let seen = read();
  if (id && handle) {
    seen = [{ id, handle }, ...seen.filter((p) => p.id !== id)].slice(0, KEEP);
    try { localStorage.setItem(KEY, JSON.stringify(seen)); } catch {}
  }

  const limit = parseInt(host.dataset.limit, 10) || 4;
  const show = seen.filter((p) => p.id !== id).slice(0, limit);
  if (show.length < MIN) { drop(); return; }

  /* We know synchronously that a row is coming, so reserve its height now.
     Injecting it after load would otherwise push the promise panel down. */
  host.classList.add('rv-reserve');

  const url = `${window.routes.search_url || '/search'}?section_id=${encodeURIComponent(host.dataset.sectionId)}`
    + `&type=product&q=${encodeURIComponent(show.map((p) => `id:${p.id}`).join(' OR '))}`;

  const load = async () => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), TIMEOUT);
    try {
      const res = await fetch(url, { signal: ctl.signal });
      if (!res.ok) { drop(); return; }
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-recently-viewed]');
      if (!fresh || !fresh.querySelector('.pcard')) { drop(); return; }

      /* Search sorts by relevance, not by when each product was seen, so put
         the cards back into the order they were actually viewed. */
      const grid = fresh.querySelector('.fcoll__grid');
      if (grid) {
        const rank = (card) => {
          const a = card.querySelector('a[href*="/products/"]');
          if (!a) return 999;
          const h = a.getAttribute('href').split('/products/')[1].split(/[?#]/)[0];
          const i = show.findIndex((p) => p.handle === h);
          return i === -1 ? 999 : i;
        };
        [...grid.children].sort((a, b) => rank(a) - rank(b)).forEach((c) => grid.appendChild(c));
      }
      host.replaceWith(fresh);
    } catch { drop(); } finally { clearTimeout(timer); }
  };

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { io.disconnect(); load(); }
    }, { rootMargin: '400px' });
    io.observe(host);
  } else {
    load();
  }
})();

document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[data-product-form]');
  if (!form) return;
  e.preventDefault();
  addToCart(form);
});

/* ---------- sticky-on-scroll header mode ----------
   Only active when the header section is set to "Sticky on scroll up":
   hides while scrolling down past the fold, reveals on any scroll up. */

const scrollHeader = document.querySelector('.site-header--scroll');
if (scrollHeader) {
  let lastY = window.scrollY;
  let ticking = false;
  const update = () => {
    const y = window.scrollY;
    if (y > 160 && y > lastY + 6) scrollHeader.classList.add('is-hidden');
    else if (y < lastY - 6 || y <= 160) scrollHeader.classList.remove('is-hidden');
    lastY = y;
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) { requestAnimationFrame(update); ticking = true; }
  }, { passive: true });
}

/* ---------- order / gift note ----------
   Saves cart.note through the Cart API so the shopper stays put. Runs for
   every instance (cart page + drawer). Falls back to a normal POST. */

function initNoteForm(noteForm) {
  if (noteForm.dataset.noteBound) return;
  noteForm.dataset.noteBound = 'true';

  const input = noteForm.querySelector('[data-note-input]');
  const used = noteForm.querySelector('[data-note-used]');
  const status = noteForm.querySelector('[data-note-status]');
  const save = noteForm.querySelector('[data-note-save]');
  if (!input) return;

  input.addEventListener('input', () => {
    if (used) used.textContent = input.value.length;
    if (status) status.hidden = true;
  });

  noteForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    save.classList.add('is-loading');
    save.disabled = true;
    try {
      const res = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: input.value }),
      });
      if (!res.ok) throw new Error('save failed');
      if (status) status.hidden = false;
      /* keep the other instance in sync without stomping what is being typed */
      document.querySelectorAll('[data-note-input]').forEach((other) => {
        if (other !== input) other.value = input.value;
      });
    } catch {
      noteForm.submit();
      return;
    } finally {
      save.classList.remove('is-loading');
      save.disabled = false;
    }
  });
}

const bindNotes = () => document.querySelectorAll('[data-note-form]').forEach(initNoteForm);
bindNotes();
/* the drawer re-renders its HTML after every cart change — rebind then */
document.addEventListener('cart:refreshed', bindNotes);

/* ---------- free gift ----------
   Claims the gift (adds the variant) and auto-removes it when the cart drops
   below the threshold. State is rendered server-side; the cart/drawer sections
   re-render on every change, so this only fires the add/remove Cart API calls. */

async function addVariant(id) {
  const res = await fetch(cartAddUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, quantity: 1 }),
  });
  if (!res.ok) return false;
  const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
  updateCount(cart.item_count);
  await Promise.all([refreshDrawer(), refreshCartPage()]);
  return true;
}

let giftBusy = false;
async function autoRemoveGift() {
  if (giftBusy) return;
  const marker = document.querySelector('[data-gift-remove]');
  if (!marker) return;
  const line = parseInt(marker.dataset.giftRemove, 10);
  if (!line) return;
  giftBusy = true;
  await changeLine(line, 0); /* changeLine refreshes + dispatches cart:refreshed */
  giftBusy = false;
}
document.addEventListener('cart:refreshed', autoRemoveGift);
autoRemoveGift();

document.addEventListener('click', async (e) => {
  const claim = e.target.closest('[data-claim-gift]');
  if (!claim) return;
  e.preventDefault();
  claim.classList.add('is-loading');
  claim.disabled = true;
  const ok = await addVariant(parseInt(claim.dataset.variantId, 10));
  if (!ok) { claim.classList.remove('is-loading'); claim.disabled = false; }
});

/* ---------- minimum-order top-up ----------
   Adds a suggested product without leaving the drawer. addVariant re-renders
   the drawer, so on success the whole top-up block is replaced server-side --
   it disappears once the basket clears the minimum, and the checkout button
   comes alive in the same paint. Only the failure path needs to restore the
   button, since on success it no longer exists. */
document.addEventListener('click', async (e) => {
  const add = e.target.closest('[data-topup-add]');
  if (!add) return;
  e.preventDefault();
  const id = parseInt(add.dataset.variantId, 10);
  if (!id) return;
  add.classList.add('is-loading');
  add.disabled = true;
  const ok = await addVariant(id);
  if (!ok) { add.classList.remove('is-loading'); add.disabled = false; }
});

/* ---------- typed quantities ----------
   Scoped to input[data-qty-value][data-line]: the PDP has its own
   [data-qty-value] with no line index, and must not be caught here.
   The same 500ms debounce that batches stepper presses batches keystrokes, so
   typing "12" sends one request rather than one per digit. */
document.addEventListener('input', (e) => {
  const field = e.target.closest('input[data-qty-value][data-line]');
  if (!field) return;
  if (field.value.trim() === '') return;   /* mid-edit — wait for a number */
  const next = Math.max(1, parseInt(field.value, 10) || 1);
  const lineEl = field.closest('.cart-line');
  if (!lineEl) return;
  queueLineChange(parseInt(field.dataset.line, 10), next, lineEl);
});

/* Leaving the field commits immediately rather than waiting out the debounce,
   and repairs a value the customer left empty or unparseable. */
document.addEventListener('change', (e) => {
  const field = e.target.closest('input[data-qty-value][data-line]');
  if (!field) return;
  const lineEl = field.closest('.cart-line');
  const parsed = parseInt(field.value, 10);
  /* repair anything below one, empty or unparseable — typing 0 is not a
     shortcut to delete, that is what the bin is for */
  if (field.value.trim() === '' || Number.isNaN(parsed) || parsed < 1) {
    field.value = 1;
    queueLineChange(parseInt(field.dataset.line, 10), 1, lineEl);
  }
  flushLineChanges();
});

/* enter should commit, not submit anything it happens to sit inside */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  const field = e.target.closest('input[data-qty-value][data-line]');
  if (!field) return;
  e.preventDefault();
  field.blur();   /* fires change, which flushes */
});

/* ---------- Christmas + everyday baskets ----------
   When the basket mixes Christmas items with everyday items, the drawer and cart
   page group them and show a notice (cart-xmas-split.liquid).
   "Check out everyday items now" sets the Christmas lines aside in localStorage,
   removes them from the basket and goes to checkout. On the next storefront page
   load (e.g. after the order) they go back in the basket. Any that can't be added
   back (sold out, removed) show in the "Saved for later" list instead.
   "Deliver everything at Christmas instead" hides the notice until the basket changes. */
const SAVED_KEY = 'tob-saved-later';
const SAVED_DAYS = 60;
const KEEP_KEY = 'tob-xmas-keep';
/* Order notes that carry the set-aside items onto the everyday order, so the
   Klaviyo "Saved Christmas items" flow can email the customer a link back. */
const XMAS_ITEMS_ATTR = 'Christmas items saved';
const XMAS_LINK_ATTR = 'Christmas order link';

function readSaved() {
  try {
    const items = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
    const cutoff = Date.now() - SAVED_DAYS * 864e5;
    return Array.isArray(items) ? items.filter((i) => i && i.id && i.savedAt > cutoff) : [];
  } catch (e) {
    return [];
  }
}
function writeSaved(items) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(items)); } catch (e) {}
}
const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* only items that couldn't go back in the basket are listed */
function renderSaved() {
  const items = readSaved().filter((i) => i.auto === false);
  document.querySelectorAll('[data-saved-later]').forEach((el) => {
    el.hidden = items.length === 0;
    if (!items.length) { el.innerHTML = ''; return; }
    const d = el.dataset;
    el.innerHTML = `
      <div class="saved-later__head">
        <p class="saved-later__title">${escapeHtml(d.heading)} (${items.length})</p>
        <button class="btn btn--small" type="button" data-saved-add-all>${escapeHtml(d.addAll)}</button>
      </div>
      <ul class="saved-later__list">${items.map((i, n) => `
        <li class="saved-later__item">
          ${i.image ? `<img src="${escapeHtml(i.image)}" width="48" height="48" alt="" loading="lazy">` : '<span class="saved-later__ph"></span>'}
          <div class="saved-later__info">
            <a href="${escapeHtml(i.url)}">${escapeHtml(i.title)}</a>
            <span>${[i.variant, i.planName, i.qty > 1 ? `× ${i.qty}` : ''].filter(Boolean).map(escapeHtml).join(' · ')}</span>
          </div>
          <div class="saved-later__btns">
            <button type="button" data-saved-add="${escapeHtml(i.id)}:${escapeHtml(i.plan || '')}">${escapeHtml(d.add)}</button>
            <button type="button" data-saved-remove="${escapeHtml(i.id)}:${escapeHtml(i.plan || '')}">${escapeHtml(d.remove)}</button>
          </div>
        </li>`).join('')}
      </ul>`;
  });
}

/* "Keep in one order" lasts until the basket's lines change */
function applyXmasKeep() {
  let kept = '';
  try { kept = sessionStorage.getItem(KEEP_KEY) || ''; } catch (e) {}
  document.querySelectorAll('[data-xmas-split]').forEach((el) => {
    el.hidden = kept !== '' && kept === el.dataset.xmasSig;
  });
}

function onCartRender() { renderSaved(); applyXmasKeep(); }
document.addEventListener('cart:refreshed', onCartRender);
onCartRender();

const savedId = (i) => `${i.id}:${i.plan || ''}`;

async function restoreSaved(ids) {
  const saved = readSaved();
  const picked = saved.filter((i) => ids.includes(savedId(i)));
  if (!picked.length) return false;
  const items = picked.map((i) => {
    const line = { id: i.id, quantity: i.qty };
    if (i.plan) line.selling_plan = i.plan;
    /* underscore properties (e.g. an old Zapiet date) belong to the old line */
    const props = Object.fromEntries(Object.entries(i.properties || {}).filter(([k]) => !k.startsWith('_')));
    if (Object.keys(props).length) line.properties = props;
    return line;
  });
  const res = await fetch(cartAddUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    /* leave them in the "Saved for later" list for the customer to retry */
    picked.forEach((i) => { i.auto = false; });
    writeSaved(saved);
    renderSaved();
    return false;
  }
  writeSaved(saved.filter((i) => !picked.includes(i)));
  /* the items are back in the basket, so this basket no longer has anything "saved" */
  await fetch(`${window.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { [XMAS_ITEMS_ATTR]: '', [XMAS_LINK_ATTR]: '' } }),
  }).catch(() => {});
  const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
  updateCount(cart.item_count);
  await Promise.all([refreshDrawer(), refreshCartPage()]);
  return true;
}

document.addEventListener('click', async (e) => {
  const save = e.target.closest('[data-xmas-save]');
  if (save) {
    e.preventDefault();
    const wrap = save.closest('[data-xmas-split]');
    let lines = [];
    try { lines = JSON.parse(wrap.querySelector('[data-xmas-items]').textContent); } catch (err) { return; }
    if (!lines.length) return;
    save.disabled = true;
    save.classList.add('is-loading');
    const res = await fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: Object.fromEntries(lines.map((l) => [l.key, 0])),
        attributes: {
          [XMAS_ITEMS_ATTR]: lines.map((l) => `${l.title}${l.variant ? ` (${l.variant})` : ''} × ${l.qty}`).join(', '),
          /* cart permalink: rebuilds the Christmas basket on any device and opens checkout
             (selling plans can't ride on permalinks, so subscriptions come back as one-offs) */
          [XMAS_LINK_ATTR]: `${window.location.origin}/cart/${lines.map((l) => `${l.id}:${l.qty}`).join(',')}`,
        },
      }),
    });
    if (!res.ok) { save.disabled = false; save.classList.remove('is-loading'); return; }
    /* merge with anything set aside earlier (same variant and plan → add the quantities) */
    const saved = readSaved();
    lines.forEach(({ key, ...line }) => {
      const same = saved.find((s) => savedId(s) === savedId(line));
      if (same) { same.qty += line.qty; same.savedAt = Date.now(); same.auto = true; }
      else saved.push({ ...line, auto: true, savedAt: Date.now() });
    });
    writeSaved(saved);
    window.location.href = `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}checkout`;
    return;
  }

  const keep = e.target.closest('[data-xmas-keep]');
  if (keep) {
    const wrap = keep.closest('[data-xmas-split]');
    try { sessionStorage.setItem(KEEP_KEY, wrap.dataset.xmasSig); } catch (err) {}
    applyXmasKeep();
    return;
  }

  const addAll = e.target.closest('[data-saved-add-all]');
  const addOne = e.target.closest('[data-saved-add]');
  if (addAll || addOne) {
    const btn = addAll || addOne;
    btn.disabled = true;
    const ids = addAll ? readSaved().filter((i) => i.auto === false).map(savedId) : [addOne.dataset.savedAdd];
    const ok = await restoreSaved(ids);
    if (!ok) btn.disabled = false;
    return;
  }

  const remove = e.target.closest('[data-saved-remove]');
  if (remove) {
    writeSaved(readSaved().filter((i) => savedId(i) !== remove.dataset.savedRemove));
    renderSaved();
  }
});

/* Christmas items set aside for an everyday checkout go back in the basket on the
   next storefront page load. Once per page, so a slow request can't double them. */
(function restorePending() {
  const ids = readSaved().filter((i) => i.auto !== false).map(savedId);
  if (ids.length) restoreSaved(ids);
})();
